import { AnalyticsUserEvent } from '@fruitful/types';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import {
    type NativeScrollEvent,
    type NativeSyntheticEvent,
    Platform,
    type ScrollView,
} from 'react-native';
import { BackButton, BackButtonIconType, TallSheet, YStack } from 'smoothie';

import TransactionDetails from '../../../banking/components/spaces/transactions/TransactionDetails';
import useGetAccountDisplayDetailsFromGlobalState from '../../../banking/hooks/useGetAccountDisplayDetailsFromGlobalState';
import { selectedTransactionV2Atom } from '../../../banking/state/SpacesAtoms';
import { type Transaction } from '../../../banking/types/Transactions';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { EnrichedTransactionDetailsView } from '../../../llm-service/components/widgets/EnrichedTransactionDetails';
import { useEnrichedTransaction } from '../../../llm-service/hooks/useEnrichedTransaction';
import { track } from '../../../logger/Logger';
import {
    FreeToSpendSheetViewAtom,
    SelectedFreeToSpendAccountSyncteraId,
} from '../../state/FreeToSpendAtoms';
import {
    type FreeToSpendDataItem,
    type FreeToSpendPeriod,
} from '../../types/FreeToSpendTypes';
import {
    DayBox,
    FreeToSpendTransactionsList,
    TransactionItem,
} from './FreeToSpendTransactionsList';

const SHEET_CLOSE_RESET_DELAY_MS = 600;

// Re-export row components for existing consumers (e.g. TransactionsWidget).
export { DayBox, TransactionItem };

const TransactionsSheet = ({
    isOpen,
    onClose,
    spendPeriod,
    allSpendData,
    budget,
    isLoadingTransactions,
    isTransactionsError = false,
    onRetryTransactions,
    enrichTransactionDetails = false,
    syncteraAccountIdOverride,
    initialSelectedPeriodKey,
}: {
    isOpen: boolean;
    onClose: () => void;
    spendPeriod: FreeToSpendPeriod;
    allSpendData: FreeToSpendDataItem[];
    budget: number | undefined;
    isLoadingTransactions: boolean;
    isTransactionsError?: boolean;
    onRetryTransactions?: () => void;
    /**
     * When true, the detail view fetches the full transaction client-side by id
     * (the rows hold lossy, agent-serialized data). Used by the Sprout chat FTS
     * widget. Requires {@link syncteraAccountIdOverride}. Defaults to false (the
     * home screen already has full transactions).
     */
    enrichTransactionDetails?: boolean;
    /**
     * Synctera account id to attribute selected transactions to, instead of the
     * global free-to-spend account atom. Used when this sheet is rendered
     * outside the home screen (e.g. the Sprout chat widget).
     */
    syncteraAccountIdOverride?: string;
    /** Optional initial bar for the list view (same key shape as the chart). */
    initialSelectedPeriodKey?: string;
}) => {
    const { trackViewOnce } = useViewEvents();
    useEffect(() => {
        if (isOpen) {
            trackViewOnce('FtsTransactions');
        }
    }, [isOpen]);

    const [freeToSpendSheetView, setFreeToSpendSheetView] = useAtom(
        FreeToSpendSheetViewAtom,
    );
    const setSelectedTransaction = useSetAtom(selectedTransactionV2Atom);
    const selectedFreeToSpendAccountSyncteraId = useAtomValue(
        SelectedFreeToSpendAccountSyncteraId,
    );

    const selectedTransactionInfo = useAtomValue(selectedTransactionV2Atom);
    const { syncteraAccountId } = selectedTransactionInfo ?? {
        transaction: null,
        syncteraAccountId: '',
    };

    const { account } = useGetAccountDisplayDetailsFromGlobalState({
        syncteraAccountId,
    });

    // Sprout chat passes lossy, agent-serialized transactions, so fetch the full
    // transaction here in the provider tree (the sheet's detail view renders in a
    // portal mounted outside AuthedUserProvider).
    const enrichedTransaction = useEnrichedTransaction({
        uuid: selectedTransactionInfo?.transaction?.uuid ?? '',
        syncteraAccountId,
        enabled:
            enrichTransactionDetails &&
            freeToSpendSheetView === 'details' &&
            selectedTransactionInfo?.transaction != null,
    });

    // Track/restore list scroll position when switching views.
    const sheetScrollYRef = useRef(0);
    const savedListScrollYRef = useRef(0);
    const contentScrollRef = useRef<ScrollView>(null);

    const handleSheetScroll = (
        event: NativeSyntheticEvent<NativeScrollEvent>,
    ) => {
        sheetScrollYRef.current = event.nativeEvent.contentOffset.y;
    };

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        sheetScrollYRef.current = 0;
        savedListScrollYRef.current = 0;

        if (Platform.OS === 'web') {
            const frameOne = requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    contentScrollRef.current?.scrollTo({
                        y: 0,
                        animated: false,
                    });
                });
            });
            return () => {
                cancelAnimationFrame(frameOne);
            };
        }

        let cancelled = false;
        const outerId = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!cancelled) {
                    contentScrollRef.current?.scrollTo({
                        y: 0,
                        animated: false,
                    });
                }
            });
        });
        return () => {
            cancelled = true;
            cancelAnimationFrame(outerId);
        };
    }, [isOpen]);

    const openTransactionDetails = (transaction: Transaction) => {
        track(AnalyticsUserEvent.FTS_TAP_TRANSACTION);
        savedListScrollYRef.current = sheetScrollYRef.current;
        setSelectedTransaction({
            transaction,
            syncteraAccountId:
                syncteraAccountIdOverride ??
                selectedFreeToSpendAccountSyncteraId,
        });
        setFreeToSpendSheetView('details');
        requestAnimationFrame(() => {
            contentScrollRef.current?.scrollTo({ y: 0, animated: false });
        });
    };

    const handleBackToList = () => {
        const restoreY = savedListScrollYRef.current;
        setFreeToSpendSheetView('list');
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                contentScrollRef.current?.scrollTo({
                    y: restoreY,
                    animated: false,
                });
            });
        });
    };

    useEffect(() => {
        if (isOpen) {
            return;
        }
        const id = setTimeout(() => {
            setFreeToSpendSheetView('list');
            savedListScrollYRef.current = 0;
            sheetScrollYRef.current = 0;
        }, SHEET_CLOSE_RESET_DELAY_MS);
        return () => {
            clearTimeout(id);
        };
    }, [isOpen, setFreeToSpendSheetView]);

    return (
        <TallSheet
            disableDrag={Platform.OS === 'web'}
            isOpen={isOpen}
            contentScrollRef={contentScrollRef}
            onContentScroll={handleSheetScroll}
            onClose={() => {
                onClose();
            }}
            title={
                freeToSpendSheetView === 'list'
                    ? 'Spend'
                    : 'Transaction details'
            }
            showCloseButton={freeToSpendSheetView === 'list'}
            headerLeftAction={
                freeToSpendSheetView === 'list' ? null : (
                    <BackButton
                        background="gray"
                        iconType={BackButtonIconType.ARROW_LEFT}
                        customBackFn={handleBackToList}
                    />
                )
            }
        >
            {freeToSpendSheetView === 'list' ? (
                <FreeToSpendTransactionsList
                    spendPeriod={spendPeriod}
                    allSpendData={allSpendData}
                    budget={budget}
                    isLoadingTransactions={isLoadingTransactions}
                    isTransactionsError={isTransactionsError}
                    onRetryTransactions={onRetryTransactions}
                    initialSelectedPeriodKey={initialSelectedPeriodKey}
                    onOpenTransaction={openTransactionDetails}
                    isActive={isOpen}
                />
            ) : enrichTransactionDetails ? (
                <YStack flex={1} marginTop={24}>
                    <EnrichedTransactionDetailsView
                        account={enrichedTransaction.account}
                        transaction={enrichedTransaction.transaction}
                        isLoading={enrichedTransaction.isLoading}
                        notFound={enrichedTransaction.notFound}
                        syncteraAccountId={syncteraAccountId}
                    />
                </YStack>
            ) : (
                <YStack flex={1} marginTop={24}>
                    <TransactionDetails account={account} />
                </YStack>
            )}
        </TallSheet>
    );
};

export default TransactionsSheet;
